import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, KeyRound, Search, ShieldCheck } from "lucide-react";

/**
 * AccessControlPage — full CRUD for property access credentials.
 * Issue fobs, mobile passes, PINs, vendor cards, or physical keys with
 * per-zone access lists. Toggle active/inactive without losing history.
 */
const CRED_TYPES = ["fob", "mobile_pass", "pin", "vendor", "key"];
const COMMON_ZONES = [
  "Main Gate", "Pool", "Gym", "Clubhouse", "Package Room", "Roof Deck",
  "Garage", "Mail Room", "Elevator", "Bike Room", "Amenity Deck",
];

export default function AccessControlPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    holder: "", unit: "", type: "fob", credential_id: "", zones: [], active: true, notes: "",
  });

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/access?property_id=${propertyId}`);
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ holder: "", unit: "", type: "fob", credential_id: "", zones: [], active: true, notes: "" });
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      holder: c.holder || "", unit: c.unit || "", type: c.type || "fob",
      credential_id: c.credential_id || "", zones: c.zones || [],
      active: !!c.active, notes: c.notes || "",
    });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.holder.trim() || !form.credential_id.trim()) {
      return toast.error("Holder and credential ID are required");
    }
    const body = {
      holder: form.holder.trim(),
      unit: form.unit.trim() || null,
      type: form.type,
      credential_id: form.credential_id.trim(),
      zones: form.zones,
      active: form.active,
      notes: form.notes.trim() || null,
    };
    try {
      if (editing) {
        await api.patch(`/access/${editing.id}`, body);
        toast.success("Credential updated");
      } else {
        await api.post(`/access?property_id=${propertyId}`, body);
        toast.success("Credential issued");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const toggleActive = async (c, active) => {
    try { await api.patch(`/access/${c.id}`, { active }); toast.success(active ? "Enabled" : "Disabled"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Revoke and delete credential ${c.credential_id} (${c.holder})?`)) return;
    try { await api.delete(`/access/${c.id}`); toast.success("Credential revoked"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const toggleZone = (zone) => {
    setForm((f) => f.zones.includes(zone)
      ? { ...f, zones: f.zones.filter((z) => z !== zone) }
      : { ...f, zones: [...f.zones, zone] });
  };

  const filtered = useMemo(() => items.filter((c) => {
    if (typeFilter !== "all" && c.type !== typeFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [c.holder, c.unit, c.credential_id, ...(c.zones || [])]
      .some((v) => (v || "").toLowerCase().includes(s));
  }), [items, q, typeFilter]);

  const kpis = useMemo(() => ({
    total: items.length,
    active: items.filter((c) => c.active).length,
    inactive: items.filter((c) => !c.active).length,
  }), [items]);

  return (
    <div>
      <PageHeader
        eyebrow="Security"
        title="Access Control"
        subtitle="Fobs, mobile passes, PIN codes, vendor cards. Revoke instantly if lost."
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-white/10">{kpis.total} total</Badge>
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">{kpis.active} active</Badge>
            {kpis.inactive > 0 && <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10">{kpis.inactive} revoked</Badge>}
            <Button size="sm" onClick={openNew} className="bg-orange-500 hover:bg-orange-600 gap-2" data-testid="ac-new-btn">
              <Plus className="h-4 w-4" /> Issue credential
            </Button>
          </div>
        }
      />

      <Card className="border-white/10 bg-card p-4 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="ac-search" className="pl-9 bg-white/[0.02] border-white/10" placeholder="Search holder, unit, credential id, zone" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="ac-type-filter" className="w-44 bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {CRED_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-white/10 bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Holder</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Zones</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} className="border-white/5" data-testid={`ac-row-${c.id}`}>
                <TableCell className="font-medium">
                  <div>{c.holder}</div>
                  {c.notes && <div className="text-[10px] text-zinc-500 truncate max-w-[180px]">{c.notes}</div>}
                </TableCell>
                <TableCell>{c.unit || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="border-white/10 capitalize">{(c.type || "").replace("_", " ")}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{c.credential_id}</TableCell>
                <TableCell className="max-w-xs">
                  <div className="flex flex-wrap gap-1">
                    {(c.zones || []).map((z) => <Badge key={z} variant="outline" className="border-white/10 text-[10px]">{z}</Badge>)}
                    {(!c.zones || c.zones.length === 0) && <span className="text-zinc-500 italic text-[11px]">none</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <Switch checked={!!c.active} onCheckedChange={(v) => toggleActive(c, v)} data-testid={`ac-toggle-${c.id}`} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`ac-edit-${c.id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-rose-300" onClick={() => remove(c)} data-testid={`ac-delete-${c.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground text-sm" data-testid="ac-empty">
                <ShieldCheck className="h-6 w-6 mx-auto mb-2 opacity-60" />
                No credentials match — issue one to a resident or vendor.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="ac-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit credential" : "Issue new credential"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Holder name</Label>
                <Input data-testid="ac-form-holder" value={form.holder} onChange={(e) => setForm({ ...form, holder: e.target.value })} placeholder="Jane Smith / ACME Cleaning" />
              </div>
              <div>
                <Label>Unit (optional)</Label>
                <Input data-testid="ac-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="12B" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="ac-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CRED_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Credential ID</Label>
                <Input data-testid="ac-form-credid" value={form.credential_id} onChange={(e) => setForm({ ...form, credential_id: e.target.value })} placeholder="FOB-4837 / #2211" />
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><KeyRound className="h-3 w-3" /> Zones granted</Label>
              <div className="flex flex-wrap gap-1 mt-2">
                {COMMON_ZONES.map((z) => (
                  <button type="button" key={z}
                          onClick={() => toggleZone(z)}
                          data-testid={`ac-zone-${z.replace(/\s+/g, "-").toLowerCase()}`}
                          className={"text-[11px] px-2 py-1 rounded-md border transition-colors " +
                            (form.zones.includes(z)
                              ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                              : "border-white/10 text-zinc-400 hover:bg-white/5")}>
                    {z}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={2} data-testid="ac-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Issued 2026-02 · replaces lost fob" />
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-white/10">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="ac-form-active" />
              <span className="text-sm">{form.active ? "Active — will unlock configured zones" : "Revoked — access blocked"}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={save} data-testid="ac-form-save">
              {editing ? "Save" : "Issue credential"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
